import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { mainTemplatesApi, protocolsApi, type MainTemplate, type MainProtocol, ApiError } from "@/api/client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MAIN_OFFLINE_FALLBACK } from "@/lib/templates";

// ---------------------------------------------------------------------------
// /templates — 主模板(v6→main 域重构)
//
// 域模型(doc/softmath/核心域.md):主模板 id 主键,引用主协议(main_protocol_id);
// protocol_type 从所选主协议 denormalize(决定下游 channel.type)。不再有
// adapter_type/groups/priority —— adapter 由绑定站点决定,groups/priority 在中转站
// 绑定页(template_relay_site)。
// ---------------------------------------------------------------------------

const inpCls = "h-9 w-full rounded-md border bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-ring";

export function MainTemplatesPage() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["main-templates"],
    queryFn: () => mainTemplatesApi.list(),
    staleTime: 30_000,
  });
  const items: MainTemplate[] = q.data?.items ?? (q.isError ? MAIN_OFFLINE_FALLBACK : []);

  // 主协议字典:提供下拉数据源 + 展示名(把 main_protocol_id / protocol_type 转可读)。
  const protocolsQ = useQuery({ queryKey: ["protocols"], queryFn: protocolsApi.list, staleTime: 5 * 60_000 });
  const protocols: MainProtocol[] = protocolsQ.data?.items ?? [];
  const protoById = (id: number) => protocols.find((p) => p.id === id);
  const fmtProtocol = (t: MainTemplate) => {
    const p = protoById(t.main_protocol_id);
    return p ? `${p.name} (${t.protocol_type})` : String(t.protocol_type);
  };

  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<MainTemplate | null>(null);
  const [pageMsg, setPageMsg] = useState<string | null>(null);

  const [search, setSearch] = useState("");
  const [enabledFilter, setEnabledFilter] = useState<"all" | "true" | "false">("all");
  const [protocolFilter, setProtocolFilter] = useState<string>("all");

  const filtered = items.filter((t) => {
    const s = search.trim().toLowerCase();
    const matchSearch =
      !s || [t.code, t.name, t.eyebrow, t.description].join(" ").toLowerCase().includes(s);
    const matchEnabled = enabledFilter === "all" || t.enabled === (enabledFilter === "true");
    const matchProtocol = protocolFilter === "all" || String(t.main_protocol_id) === protocolFilter;
    return matchSearch && matchEnabled && matchProtocol;
  });

  const protocolOptions = Array.from(
    new Set(items.map((t) => t.main_protocol_id).filter((v) => v != null))
  ).sort((a, b) => a - b);

  const deleteMut = useMutation({
    mutationFn: (id: number) => mainTemplatesApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["main-templates"] });
      setPageMsg("已删除模板");
    },
    onError: (e) => setPageMsg(e instanceof ApiError ? e.message : "删除失败"),
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <h1 className="text-xl font-semibold">主模板</h1>
          <p className="mt-0.5 text-xs text-muted-foreground">
            分发预设(main_template),引用主协议(原厂)。协议类型决定下游渠道的 channel.type;
            适配器由绑定站点决定,分组/优先级在「中转站绑定」页维护。
          </p>
        </div>
        <div className="ml-auto flex items-center gap-3 text-xs text-muted-foreground">
          <span>
            <strong className="text-foreground">{filtered.length}</strong> / {items.length} 个模板
          </span>
          {q.isError && <Badge variant="warning">API 不可达,显示离线占位</Badge>}
          <Button size="sm" onClick={() => setCreating(true)}>
            <Plus className="mr-1 h-4 w-4" /> 新增模板
          </Button>
        </div>
      </div>

      {pageMsg && (
        <div className="rounded-md border border-border bg-card px-3 py-2 text-xs text-muted-foreground">
          {pageMsg}
          <button type="button" className="ml-2 text-muted-foreground hover:text-foreground" onClick={() => setPageMsg(null)} aria-label="关闭提示">×</button>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <Input
          className="max-w-[260px]"
          placeholder="搜索 code / 名称 / 关键字…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          className="h-9 rounded-md border bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-ring"
          value={enabledFilter}
          onChange={(e) => setEnabledFilter(e.target.value as "all" | "true" | "false")}
        >
          <option value="all">启用:全部</option>
          <option value="true">启用</option>
          <option value="false">禁用</option>
        </select>
        <select
          className="h-9 rounded-md border bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-ring"
          value={protocolFilter}
          onChange={(e) => setProtocolFilter(e.target.value)}
        >
          <option value="all">协议:全部</option>
          {protocolOptions.map((id) => {
            const p = protoById(id);
            return (
              <option key={id} value={String(id)}>{p ? `${p.name} (${id})` : `主协议 #${id}`}</option>
            );
          })}
        </select>
      </div>

      {filtered.length === 0 ? (
        <div className="rounded-xl border bg-card p-8 text-center text-sm text-muted-foreground shadow-sm">
          {q.isLoading ? "加载中…" : items.length === 0 ? "暂无中转模板。点击右上「+ 新增模板」开始录入。" : "没有匹配筛选条件的模板。"}
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border bg-card shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b bg-muted/50 text-left text-xs font-medium text-muted-foreground">
                <tr>
                  <th className="px-3 py-2.5">Code</th>
                  <th className="px-3 py-2.5">名称</th>
                  <th className="px-3 py-2.5">适配器</th>
                  <th className="px-3 py-2.5">协议</th>
                  <th className="px-3 py-2.5">启用</th>
                  <th className="px-3 py-2.5 text-center">操作</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((t) => (
                  <tr key={t.id} className="border-b hover:bg-muted/30">
                    <td className="px-3 py-2.5 font-mono text-xs">{t.code}</td>
                    <td className="px-3 py-2.5">
                      <div className="font-medium">{t.name}</div>
                      {t.eyebrow && <div className="text-[11px] text-muted-foreground">{t.eyebrow}</div>}
                    </td>
                    <td className="px-3 py-2.5"><Badge variant="muted">{protoById(t.main_protocol_id)?.code ?? `#${t.main_protocol_id}`}</Badge></td>
                    <td className="px-3 py-2.5 font-mono text-xs">{fmtProtocol(t)}</td>
                    <td className="px-3 py-2.5">{t.enabled ? <Badge variant="success">启用</Badge> : <Badge variant="muted">禁用</Badge>}</td>
                    <td className="px-3 py-2.5 text-center">
                      <button type="button" className="rounded p-1 text-muted-foreground hover:bg-accent" onClick={() => setEditing(t)} title="修改模板">
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        className="rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                        onClick={() => {
                          if (confirm(`确认删除模板「${t.name}」(code=${t.code})?`)) {
                            deleteMut.mutate(t.id);
                          }
                        }}
                        title="删除模板"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {creating && (
        <MainTplModal
          mode="create"
          protocols={protocols}
          onClose={() => setCreating(false)}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ["main-templates"] });
            setCreating(false);
            setPageMsg("已新增模板");
          }}
        />
      )}

      {editing && (
        <MainTplModal
          mode="edit"
          template={editing}
          protocols={protocols}
          onClose={() => setEditing(null)}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ["main-templates"] });
            setEditing(null);
            setPageMsg("已保存");
          }}
        />
      )}
    </div>
  );
}

function MainTplModal({
  mode,
  template,
  protocols,
  onClose,
  onSaved,
}: {
  mode: "create" | "edit";
  template?: MainTemplate;
  protocols: MainProtocol[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = mode === "edit" && template != null;

  const [form, setForm] = useState({
    main_protocol_id: template?.main_protocol_id ?? (protocols[0]?.id ?? 0),
    code: template?.code ?? "",
    name: template?.name ?? "",
    eyebrow: template?.eyebrow ?? "",
    description: template?.description ?? "",
    models: template?.models ?? "",
    model_mapping: template?.model_mapping ?? "",
    enabled: template?.enabled ?? true,
  });
  const [codeError, setCodeError] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: async (): Promise<MainTemplate | { updated: number }> => {
      if (!form.main_protocol_id) {
        throw new Error("请选择主协议");
      }
      const body = {
        main_protocol_id: Number(form.main_protocol_id),
        code: form.code.trim(),
        name: form.name.trim(),
        eyebrow: form.eyebrow.trim(),
        description: form.description.trim(),
        models: form.models.trim(),
        model_mapping: form.model_mapping.trim(),
        enabled: form.enabled,
      };
      return isEdit
        ? mainTemplatesApi.update(template!.id, body)
        : mainTemplatesApi.create(body);
    },
    onSuccess: onSaved,
    onError: (e) => {
      if (e instanceof ApiError && e.status === 409) {
        setCodeError(e.message);
        setErr(null);
      } else if (e instanceof Error && !(e instanceof ApiError)) {
        setCodeError(null);
        setErr(e.message);
      } else {
        setCodeError(null);
        setErr(e instanceof ApiError ? e.message : "保存失败");
      }
    },
  });

  const selectedProto = protocols.find((p) => p.id === Number(form.main_protocol_id));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-2xl rounded-xl border bg-card p-5 shadow-lg" onClick={(e) => e.stopPropagation()}>
        <h2 className="mb-4 text-base font-semibold">{isEdit ? "修改主模板" : "新增主模板"}</h2>
        {err && <div className="mb-3 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-xs text-destructive">{err}</div>}
        <form
          className="grid grid-cols-2 gap-3"
          onSubmit={(e) => { e.preventDefault(); setErr(null); setCodeError(null); mut.mutate(); }}
        >
          <div className="space-y-1">
            <Label>Code <span className="text-destructive">*</span></Label>
            <Input
              className={`${inpCls} ${codeError ? "border-destructive focus-visible:ring-destructive" : ""}`}
              value={form.code}
              onChange={(e) => { setForm({ ...form, code: e.target.value }); if (codeError) setCodeError(null); }}
              placeholder="例:mi-openai"
              required
              disabled={isEdit}
            />
            {codeError && <p className="text-xs text-destructive">{codeError}</p>}
          </div>
          <div className="space-y-1">
            <Label>显示名 <span className="text-destructive">*</span></Label>
            <Input className={inpCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
          </div>
          <div className="space-y-1">
            <Label>主协议(原厂) <span className="text-destructive">*</span></Label>
            <select className={inpCls} value={form.main_protocol_id} onChange={(e) => setForm({ ...form, main_protocol_id: Number(e.target.value) })}>
              {protocols.length === 0 && <option value={0}>— 无主协议,请先在系统设置录入 —</option>}
              {protocols.map((p) => (
                <option key={p.id} value={p.id}>{p.name} ({p.code})</option>
              ))}
            </select>
            <p className="text-[11px] text-muted-foreground">
              协议类型(channel.type)= {selectedProto ? selectedProto.protocol_type : "—"}(从主协议派生)
            </p>
          </div>
          <div className="space-y-1">
            <Label>Eyebrow(分组标签)</Label>
            <Input className={inpCls} value={form.eyebrow} onChange={(e) => setForm({ ...form, eyebrow: e.target.value })} />
          </div>
          <div className="space-y-1 col-span-2">
            <Label>模型列表(逗号分隔)</Label>
            <Input className={inpCls} value={form.models} onChange={(e) => setForm({ ...form, models: e.target.value })} placeholder="gpt-4o,gpt-4o-mini" />
          </div>
          <div className="space-y-1 col-span-2">
            <Label>Model Mapping(JSON,留空表示不重写)</Label>
            <textarea
              className="min-h-[80px] w-full rounded-md border bg-background px-3 py-2 font-mono text-xs outline-none focus:ring-2 focus:ring-ring"
              value={form.model_mapping}
              onChange={(e) => setForm({ ...form, model_mapping: e.target.value })}
              placeholder='{"gpt-4":"gpt-4-turbo"}'
            />
          </div>
          <div className="space-y-1 col-span-2">
            <Label>描述</Label>
            <Input className={inpCls} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
          <div className="col-span-2 space-y-1">
            <Label>启用</Label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} className="h-4 w-4 rounded border" />
              {form.enabled ? "启用 — 可被分发命中" : "禁用 — 不再被分发命中"}
            </label>
          </div>
          <div className="col-span-2 flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>取消</Button>
            <Button type="submit" disabled={mut.isPending}>{mut.isPending ? "保存中…" : "保存"}</Button>
          </div>
        </form>
      </div>
    </div>
  );
}
